import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  EmailOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
  PersonAddOutlined,
  ArrowForward,
  LoginOutlined,
  VerifiedUserOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router";
import { userLogin, getCompanyProfile } from "../Services/Services";
import {
  storeToken,
  storeResponse,
  removeToken,
  removeDivision,
} from "../Services/LocalStorageService";
import { startTokenWatcher } from "../Services/tokenHelper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const DESKTOP_BG_URL =
  "https://images.pexels.com/photos/7867332/pexels-photo-7867332.jpeg";
const MOBILE_BG_URL =
  "https://images.pexels.com/photos/7867332/pexels-photo-7867332.jpeg";

const COMPANY_LOGO = "/sid_logo.png";

function LoginPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState(
    "SIDDHARTH INDUSTRIES PVT. LTD."
  );

  const bgImageUrl = isMobile ? MOBILE_BG_URL : DESKTOP_BG_URL;

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const data = await getCompanyProfile();
        if (data) {
          if (data.company_name) setCompanyName(data.company_name);
        }
      } catch (error) {
        console.warn("Could not load company profile, using defaults.");
      }
    };
    fetchCompany();
  }, []);

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!email || !password) {
    setError("⚠️ Please fill in both email and password.");
    toast.warning("⚠️ Please fill in both email and password.");
    return;
  }
  try {
    setLoading(true);
    setError("");
    const response = await userLogin({ email, password });

    if (response?.access) {
      removeToken();
      storeToken(response.access);
      storeResponse(response.user);

      if (response.user?.department) {
        localStorage.setItem("department", response.user.department);
      } else {
        localStorage.removeItem("department");
      }

      removeDivision();
      if (response.user?.division) {
        localStorage.setItem("division", JSON.stringify(response.user.division));
      } else {
        const defaultDivision = {
          id: 1,
          name: "Default Division",
          code: "DEFAULT",
        };
        localStorage.setItem("division", JSON.stringify(defaultDivision));
      }

      const permissionKey = `user_permissions_${response.user.id}`;
      const perms = response.permissions || [];
      localStorage.setItem(permissionKey, JSON.stringify(perms));

      toast.success("Login successful! Redirecting...");
      startTokenWatcher();
      setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 500);
    } else {
      setError("Invalid credentials. Please try again.");
      toast.error("Invalid credentials. Please try again.");
    }
  } catch (err) {
    const detail = err.response?.data?.detail;
    if (detail?.toLowerCase().includes("inactive")) {
      setError("User is inactive. Please contact admin.");
      toast.error("User is inactive. Please contact admin.");
    } else if (detail?.toLowerCase().includes("invalid")) {
      setError(" Invalid credentials.");
      toast.error(" Invalid credentials.");
    } else {
      const message = detail || err.message || "Something went wrong.";
      setError(` ${message}`);
      toast.error(` ${message}`);
    }
  } finally {
    setLoading(false);
  }
};

  const iconWrapperSx = {
    backgroundColor: "#f0f0f0",
    borderRadius: "50%",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: `
          linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), 
          url(${bgImageUrl})
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: { xs: "scroll", md: "fixed" },
        overflowY: "auto",
        // ✅ Mobile par sirf vertical padding, horizontal tight rakho
        px: { xs: 2, sm: 3, md: 5, lg: 8 },
        py: { xs: 3, sm: 3 },
        boxSizing: "border-box",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      <Box
        sx={{
          width: "100%",
          maxWidth: "1200px",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: { xs: "center", md: "space-between" },
          gap: { xs: 0, md: 6, lg: 8 },
          minHeight: { xs: "auto", md: "auto" },
        }}
      >
        {/* ════════════════════════════════════ */}
        {/* LEFT HERO — tablet+ only            */}
        {/* ════════════════════════════════════ */}
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flex: 1,
            color: "#ffffff",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: { sm: "center", md: "flex-start" },
            maxWidth: { md: "540px" },
            ml: { sm: 0, md: "-2%", lg: "-5%" },
            textAlign: { sm: "center", md: "left" },
            order: 1,
            width: "100%",
          }}
        >
          <Typography
            variant="h3"
            fontWeight="800"
            sx={{
              fontFamily: "'Poppins', sans-serif",
              letterSpacing: "-0.5px",
              lineHeight: 1.2,
              fontSize: { sm: "2.4rem", md: "3rem", lg: "3.2rem" },
              textShadow: "0 4px 20px rgba(0,0,0,0.8)",
              color: "#ffffff",
            }}
          >
            WELCOME TO
          </Typography>

          <Box
            sx={{
              mt: 0.5,
              mb: 0.5,
              display: "flex",
              justifyContent: { sm: "center", md: "flex-start" },
              width: "100%",
            }}
          >
            <Box
              component="img"
              src={COMPANY_LOGO}
              alt={companyName}
              sx={{
                maxHeight: { sm: 120, md: 160, lg: 150 },
                maxWidth: { sm: "75%", md: "85%" },
                objectFit: "contain",
                filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.5))",
              }}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "block";
              }}
            />
            <Typography
              variant="h4"
              fontWeight="800"
              sx={{
                display: "none",
                background:
                  "linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontFamily: "'Poppins', sans-serif",
                letterSpacing: "0.5px",
                fontSize: { sm: "1.6rem", md: "2rem", lg: "2.3rem" },
                filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.7))",
              }}
            >
              {companyName}
            </Typography>
          </Box>

          <Typography
            sx={{
              mt: 1.5,
              color: "rgba(255, 255, 255, 0.9)",
              lineHeight: 1.5,
              fontSize: { sm: "0.9rem", md: "1rem" },
              fontWeight: 300,
              textShadow: "0 2px 8px rgba(0,0,0,0.6)",
              maxWidth: { md: "460px" },
              mx: { sm: "auto", md: 0 },
            }}
          >
            Delivering world-class material handling solutions since 2006. We
            focus on quality, safety, and innovation to drive industrial
            excellence.
          </Typography>

          <Button
            href="/register"
            startIcon={<PersonAddOutlined sx={{ color: "#ffffff" }} />}
            endIcon={<ArrowForward sx={{ color: "#ffffff" }} />}
            sx={{
              mt: 2.5,
              px: { sm: 4 },
              py: { sm: 1.1 },
              borderRadius: "8px",
              color: "#ffffff",
              fontWeight: 700,
              fontSize: { sm: "0.8rem", md: "0.85rem" },
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              backgroundColor: "#f59e0b",
              transition: "all 0.3s ease",
              alignSelf: { sm: "center", md: "flex-start" },
              "&:hover": {
                backgroundColor: "#d97706",
                color: "#ffffff",
              },
            }}
          >
            Register
          </Button>
        </Box>

        {/* ════════════════════════════════════ */}
        {/* RIGHT LOGIN CARD                    */}
        {/* ════════════════════════════════════ */}
        <Paper
          elevation={6}
          sx={{
            // ✅ KEY FIX: Mobile par fixed max-width aur auto margin
            width: "100%",
            maxWidth: { xs: "360px", sm: "380px", md: "390px", lg: "420px" },
            borderRadius: { xs: "16px", sm: "20px", md: "24px" },
            // ✅ Mobile par compact padding
            p: { xs: 2, sm: 2.5, md: 3, lg: 3.5 },
            backgroundColor: "rgba(255, 255, 255, 0.97)",
            backdropFilter: "blur(8px)",
            boxShadow: "0 25px 60px rgba(0, 0, 0, 0.5)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            border: "1px solid rgba(255,255,255,0.2)",
            flexShrink: 0,
            order: 2,
            // ✅ Auto margin center karta hai mobile par
            mx: "auto",
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              textAlign: "center",
              mb: 1,
              width: "100%",
            }}
          >
            <Box
              component="img"
              src={COMPANY_LOGO}
              alt={companyName}
              sx={{
                // ✅ Mobile par logo bhi thoda chota
                maxHeight: { xs: 60, sm: 80, md: 95 },
                maxWidth: "80%",
                objectFit: "contain",
                mb: 0.5,
              }}
              onError={(e) => {
                e.target.style.display = "none";
                if (e.target.nextSibling) {
                  e.target.nextSibling.style.display = "block";
                }
              }}
            />
            {/* Fallback text */}
            <Typography
              variant="h6"
              sx={{
                display: "none",
                fontWeight: 700,
                color: "#0f172a",
                letterSpacing: "0.3px",
                fontSize: { xs: "0.85rem", sm: "1rem", md: "1.2rem" },
              }}
            >
              {companyName}
            </Typography>
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            fontWeight="700"
            color="#0f172a"
            sx={{
              fontSize: { xs: "1rem", sm: "1.2rem", md: "1.4rem" },
              letterSpacing: "-0.3px",
              textAlign: "center",
            }}
          >
            Login Account
          </Typography>

          <Typography
            variant="body2"
            color="#64748b"
            sx={{
              fontSize: { xs: "0.72rem", sm: "0.8rem" },
              mb: { xs: 1.5, sm: 2 },
              textAlign: "center",
            }}
          >
            Sign in to access your dashboard
          </Typography>

          {/* Error Box */}
          {error && (
            <Box
              sx={{
                width: "100%",
                mb: 1.5,
                p: 1,
                borderRadius: "8px",
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
              }}
            >
              <Typography
                color="#dc2626"
                align="center"
                variant="caption"
                display="block"
                fontWeight="600"
                sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
              >
                {error}
              </Typography>
            </Box>
          )}

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            style={{ width: "100%" }}
            noValidate
            autoComplete="on"
          >
            <TextField
              fullWidth
              placeholder="Username or Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              inputProps={{
                inputMode: "email",
                style: { fontSize: 16 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={iconWrapperSx}>
                      <EmailOutlined
                        sx={{ color: "#64748b", fontSize: "1.1rem" }}
                      />
                    </Box>
                  </InputAdornment>
                ),
              }}
              sx={roundedInputStyle}
            />

            <TextField
              fullWidth
              placeholder="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              inputProps={{ style: { fontSize: 16 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={iconWrapperSx}>
                      <LockOutlined
                        sx={{ color: "#64748b", fontSize: "1.1rem" }}
                      />
                    </Box>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ p: 1 }}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      <Box sx={iconWrapperSx}>
                        {showPassword ? (
                          <VisibilityOff
                            sx={{ color: "#64748b", fontSize: "1.1rem" }}
                          />
                        ) : (
                          <Visibility
                            sx={{ color: "#64748b", fontSize: "1.1rem" }}
                          />
                        )}
                      </Box>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ ...roundedInputStyle, mt: 1.5 }}
            />

            <Button
              type="submit"
              fullWidth
              disabled={loading}
              startIcon={!loading && <LoginOutlined />}
              sx={{
                mt: 2,
                py: { xs: 1.1, sm: 1.2, md: 1.3 },
                backgroundColor: "#f59e0b",
                color: "#ffffff",
                fontWeight: "700",
                fontSize: { xs: "0.82rem", sm: "0.85rem", md: "0.9rem" },
                borderRadius: "10px",
                textTransform: "none",
                boxShadow: "0 6px 18px rgba(245, 158, 11, 0.35)",
                transition: "all 0.25s ease",
                "&:hover": {
                  backgroundColor: "#d97706",
                  boxShadow: "0 8px 22px rgba(245, 158, 11, 0.45)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Login"
              )}
            </Button>
          </form>

          {/* ✅ Mobile only — Register link */}
          <Typography
            sx={{
              display: { xs: "block", sm: "none" },
              mt: 1.5,
              fontSize: "0.75rem",
              color: "#64748b",
              textAlign: "center",
            }}
          >
            New here?{" "}
            <a
              href="/register"
              style={{
                color: "#f59e0b",
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              Create an account
            </a>
          </Typography>

          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.6,
              mt: 2,
              pt: 1.5,
              borderTop: "1px solid #e2e8f0",
              width: "100%",
              flexWrap: "wrap",
            }}
          >
            <VerifiedUserOutlined
              sx={{ fontSize: "0.75rem", color: "#b45309" }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "#0f172a",
                fontSize: { xs: "0.58rem", sm: "0.65rem", md: "0.7rem" },
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              Powered by{" "}
              <a
                href="https://www.yashvitech.com"
                target="_blank"
                rel="noreferrer"
                style={{
                  color: "#f59e0b",
                  textDecoration: "none",
                  fontWeight: 700,
                  borderBottom: "2px solid #f59e0b",
                  paddingBottom: "1px",
                }}
              >
                Yashvitech IT Solution Pvt. Ltd.
              </a>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

const roundedInputStyle = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff !important",
    borderRadius: "10px",
    "& input": {
      backgroundColor: "#ffffff !important",
      // ✅ Mobile par tight padding
      padding: { xs: "11px 10px", sm: "11px 14px" },
      borderRadius: "10px",
    },
    "& fieldset": {
      borderColor: "#e2e8f0",
      borderWidth: "1.5px",
    },
    "&:hover fieldset": {
      borderColor: "#cbd5e1",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#f59e0b",
      borderWidth: "2px",
    },
  },
};

export default LoginPage;